import * as React from 'react';

export const DARK_THEMES = [
  { id: 'frost', label: 'Иней' },
  { id: 'chrome', label: 'Хром' },
  { id: 'graphite', label: 'Графит' },
] as const;

export const LIGHT_THEMES = [
  { id: 'mist', label: 'Туман' },
  { id: 'paper', label: 'Бумага' },
  { id: 'pachca', label: 'Пачка' },
] as const;

export const THEME_MODES = [
  { id: 'dark', label: 'Тёмная' },
  { id: 'light', label: 'Светлая' },
  { id: 'auto', label: 'Как в системе' },
] as const;

export type DarkThemeId = (typeof DARK_THEMES)[number]['id'];
export type LightThemeId = (typeof LIGHT_THEMES)[number]['id'];
export type ThemeId = DarkThemeId | LightThemeId;
export type ThemeMode = (typeof THEME_MODES)[number]['id'];
export type ThemeScheme = 'dark' | 'light';

export interface ThemeSetting {
  mode: ThemeMode;
  dark: DarkThemeId;
  light: LightThemeId;
}

export interface ThemeState {
  setting: ThemeSetting;
  scheme: ThemeScheme;
  themeId: ThemeId;
}

export const STORAGE_KEY = 'frostpod.theme';
export const DEFAULT_SETTING: ThemeSetting = { mode: 'auto', dark: 'graphite', light: 'mist' };

const DARK_QUERY = '(prefers-color-scheme: dark)';

function oneOf<T extends string>(options: readonly { id: T }[], value: unknown, fallback: T): T {
  return options.find((option) => option.id === value)?.id ?? fallback;
}

export function parseThemeSetting(raw: string | null): ThemeSetting {
  if (raw === null) return DEFAULT_SETTING;
  const legacy = DARK_THEMES.find((theme) => theme.id === raw);
  if (legacy !== undefined) return { ...DEFAULT_SETTING, mode: 'dark', dark: legacy.id };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_SETTING;
    const fields = parsed as Record<string, unknown>;
    return {
      mode: oneOf(THEME_MODES, fields.mode, DEFAULT_SETTING.mode),
      dark: oneOf(DARK_THEMES, fields.dark, DEFAULT_SETTING.dark),
      light: oneOf(LIGHT_THEMES, fields.light, DEFAULT_SETTING.light),
    };
  } catch {
    return DEFAULT_SETTING;
  }
}

export function resolveThemeState(setting: ThemeSetting, systemDark: boolean): ThemeState {
  const scheme: ThemeScheme = setting.mode === 'auto' ? (systemDark ? 'dark' : 'light') : setting.mode;
  return { setting, scheme, themeId: scheme === 'dark' ? setting.dark : setting.light };
}

function systemPrefersDark(): boolean {
  return typeof matchMedia === 'function' ? matchMedia(DARK_QUERY).matches : true;
}

function readStoredRaw(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(setting: ThemeSetting): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(setting));
  } catch {
    return;
  }
}

function resolveCssColor(value: string): string {
  const context = document.createElement('canvas').getContext?.('2d');
  if (!context) return value;
  context.fillStyle = '#000000';
  context.fillStyle = value;
  return context.fillStyle;
}

function paintMobileChrome(scheme: ThemeState['scheme']): void {
  const bar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
  bar?.setAttribute('content', scheme === 'dark' ? 'black-translucent' : 'default');
  const background = getComputedStyle(document.documentElement).getPropertyValue('--background').trim();
  if (background) document.querySelector('meta[name="theme-color"]')?.setAttribute('content', resolveCssColor(background));
}

function paint(state: ThemeState): void {
  const root = document.documentElement;
  root.dataset.theme = state.themeId;
  root.dataset.mode = state.scheme;
  root.style.colorScheme = state.scheme;
  paintMobileChrome(state.scheme);
}

let current: ThemeState = resolveThemeState(parseThemeSetting(readStoredRaw()), systemPrefersDark());
let watching = false;
const listeners = new Set<() => void>();

function commit(next: ThemeState): void {
  current = next;
  paint(next);
  listeners.forEach((listener) => listener());
}

function watchSystemScheme(): void {
  if (watching || typeof matchMedia !== 'function') return;
  watching = true;
  matchMedia(DARK_QUERY).addEventListener('change', (event) => {
    commit(resolveThemeState(current.setting, event.matches));
  });
}

export function applyStoredTheme(): void {
  const raw = readStoredRaw();
  const setting = parseThemeSetting(raw);
  if (raw !== null && raw !== JSON.stringify(setting)) writeStored(setting);
  commit(resolveThemeState(setting, systemPrefersDark()));
  watchSystemScheme();
}

export function updateThemeSetting(patch: Partial<ThemeSetting>): void {
  const setting = { ...current.setting, ...patch };
  writeStored(setting);
  commit(resolveThemeState(setting, systemPrefersDark()));
}

export function toggleThemeScheme(): void {
  updateThemeSetting({ mode: current.scheme === 'dark' ? 'light' : 'dark' });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): ThemeState {
  return current;
}

export function useThemeState(): ThemeState {
  return React.useSyncExternalStore(subscribe, snapshot);
}
